import React, { useMemo, useState } from "react";
import { Badge, Button, Col, Form, Modal, Row, Table } from "react-bootstrap";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import HelpTip from "app/orderTool/components/HelpTip";
import { centimesToCurrency } from "helper/globalHelper";
import {
  computeResolvedCost,
  normalizeDateYmd,
  resolveGroupForDeliveryDate,
  resolvePackageForDeliveryDate,
} from "helper/packageRotationHelper";
import { frDate } from "./slotLabels";

const MONTH_LABELS = ["Janv", "Févr", "Mars", "Avril", "Mai", "Juin", "Juil", "Août", "Sept", "Oct", "Nov", "Déc"];

const sortedEntriesAsc = (slot) =>
  [...(slot.entries || [])].sort((a, b) => {
    const cmp = String(a.effective_from || "").localeCompare(String(b.effective_from || ""));
    return cmp !== 0 ? cmp : (a.id || 0) - (b.id || 0);
  });

const resolveAt = (entries, ymd) => {
  let resolved = null;
  entries.forEach((e) => {
    const from = normalizeDateYmd(e.effective_from);
    if (from && from <= ymd) resolved = e;
  });
  return resolved;
};

/**
 * Groupe = préfixe avant « — » (« Cocktail sucré », « Collation »…), sinon le
 * premier mot (« Quiche »), pour retrouver les familles du calendrier de rotation.
 */
const groupOf = (slot) => {
  const name = slot.name || "";
  if (name.includes(" — ")) return name.split(" — ")[0].trim();
  return name.split(" ")[0].trim();
};

const shortName = (slot) => {
  const name = slot.name || "";
  return name.includes(" — ") ? name.split(" — ").slice(1).join(" — ").trim() : name;
};

/**
 * Vue d'ensemble de la carte : lignes = slots (groupés par famille),
 * colonnes = mois de l'année choisie, cellule = produit actif au 1er du mois
 * + bascules en cours de mois (« → produit (12/10) »). Les cellules où la
 * carte change par rapport au mois précédent sont surlignées.
 *
 * Un clic sur une cellule ouvre l'édition : changer le produit ou la date de
 * fin de la rotation en place, ou créer une nouvelle rotation à partir de ce
 * mois — sans quitter la vue.
 */
const CarteOverview = ({
  slots,
  onSelectSlot,
  products = [],
  productParts = [],
  packages = [],
  productGroups = [],
  onUpdateEntry,
  onCreateEntry,
  onUpdatePeriod,
}) => {
  const [year, setYear] = useState(new Date().getFullYear());
  // Éditeur de cellule : { slot, entry|null, itemId, from, until, mode: 'update'|'create' }
  const [cellEdit, setCellEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  // Édition d'une période globale depuis les chips : { old_from, old_until, new_from, new_until, count }
  const [periodModal, setPeriodModal] = useState(null);

  const nowYm = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`;

  const rows = useMemo(() => {
    const grouped = {};
    [...slots]
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
      .forEach((slot) => {
        const entries = sortedEntriesAsc(slot);
        const cells = [];
        let previousName = resolveAt(entries, `${year - 1}-12-31`)?.name || null;
        for (let m = 1; m <= 12; m++) {
          const mm = String(m).padStart(2, "0");
          const monthStart = `${year}-${mm}-01`;
          const monthEnd = `${year}-${mm}-31`;
          const atStart = resolveAt(entries, monthStart);
          const switches = entries.filter((e) => {
            const from = normalizeDateYmd(e.effective_from);
            return from && from > monthStart && from <= monthEnd;
          });
          const finalEntry = switches.length ? switches[switches.length - 1] : atStart;
          cells.push({
            ym: `${year}-${mm}`,
            entry: atStart || null,
            startName: atStart?.name || null,
            switches: switches.map((e) => ({
              name: e.name,
              day: normalizeDateYmd(e.effective_from).slice(8, 10),
            })),
            changed: (atStart?.name || null) !== previousName || switches.length > 0,
          });
          previousName = finalEntry?.name || null;
        }
        const group = groupOf(slot);
        if (!grouped[group]) grouped[group] = [];
        grouped[group].push({ slot, cells });
      });
    return grouped;
  }, [slots, year]);

  const groupNames = Object.keys(rows).sort((a, b) => a.localeCompare(b));

  // Périodes globales de rotation : couples début / fin distincts portés par
  // les rotations de l'année affichée, tous slots confondus — les « cartes »
  // (Hiver, Été, Automne…) plus les rotations isolées (viennoiseries…).
  const periods = useMemo(() => {
    const map = new Map();
    slots.forEach((slot) => {
      (slot.entries || []).forEach((e) => {
        const from = normalizeDateYmd(e.effective_from);
        if (!from || from.slice(0, 4) !== String(year)) return;
        const until = normalizeDateYmd(e.effective_until);
        const key = `${from}|${until || ""}`;
        if (!map.has(key)) map.set(key, { from, until: until || null, count: 0 });
        map.get(key).count += 1;
      });
    });
    return [...map.values()].sort(
      (a, b) => String(a.from).localeCompare(String(b.from)) || (b.count - a.count)
    );
  }, [slots, year]);

  const savePeriodModal = async () => {
    if (!periodModal?.new_from || !onUpdatePeriod) return;
    setSaving(true);
    const resp = await onUpdatePeriod(periodModal);
    setSaving(false);
    if (resp) setPeriodModal(null);
  };

  // Marges des offres à slots au fil de l'année : pour chaque forfait / groupe
  // actif portant au moins un slot, la composition est résolue au 1er de chaque
  // mois et le coût matière recalculé — le prix de vente de la fiche est fixe,
  // c'est le coût (donc la marge) qui tourne avec la carte.
  const marginRows = useMemo(() => {
    const productsById = {};
    (products || []).forEach((p) => { productsById[p.id] = p; });
    const partsById = {};
    (productParts || []).forEach((p) => { partsById[p.id] = p; });

    const offers = [
      ...(packages || [])
        .filter((p) => p.active !== false && (p.slot_lines || []).length > 0)
        .map((p) => ({ ref: p, isGroup: false, resolver: resolvePackageForDeliveryDate })),
      ...(productGroups || [])
        .filter((g) => g.active !== false && (g.slot_lines || []).length > 0)
        .map((g) => ({ ref: g, isGroup: true, resolver: resolveGroupForDeliveryDate })),
    ].sort((a, b) => (a.ref.name || "").localeCompare(b.ref.name || ""));

    return offers.map(({ ref, isGroup, resolver }) => {
      const sale = ref.price !== null && ref.price !== undefined ? Number(ref.price) : null;
      const cells = [];
      let prevCost = null;
      for (let m = 1; m <= 12; m++) {
        const mm = String(m).padStart(2, "0");
        const resolved = resolver(ref, `${year}-${mm}-01`, slots) || ref;
        const c = computeResolvedCost(resolved, productsById, partsById);
        const cost = c ? c.cost : null;
        const margin = sale !== null && cost !== null ? sale - cost : null;
        const rate = margin !== null && sale > 0 ? (margin / sale) * 100 : null;
        cells.push({
          ym: `${year}-${mm}`,
          cost,
          complete: c ? c.complete : false,
          margin,
          rate,
          unresolved: (resolved._unresolvedSlots || []).length,
          changed: prevCost !== null && cost !== null && cost !== prevCost,
        });
        if (cost !== null) prevCost = cost;
      }
      return { ref, isGroup, sale, cells };
    });
  }, [packages, productGroups, products, productParts, slots, year]);

  const canEdit = !!(onUpdateEntry && onCreateEntry);

  const openCellEdit = (slot, cell) => {
    if (!canEdit) return;
    const entry = cell.entry;
    const monthStart = `${cell.ym}-01`;
    setCellEdit({
      slot,
      entry: entry || null,
      mode: entry ? "update" : "create",
      itemId: entry?.item_id ?? null,
      from: entry ? normalizeDateYmd(entry.effective_from) : monthStart,
      until: entry?.effective_until ? normalizeDateYmd(entry.effective_until) : "",
      monthStart,
    });
  };

  const setCellEditMode = (mode) => {
    setCellEdit((c) => ({
      ...c,
      mode,
      from: mode === "create" ? c.monthStart : normalizeDateYmd(c.entry?.effective_from) || c.monthStart,
      until: mode === "create" ? "" : c.entry?.effective_until ? normalizeDateYmd(c.entry.effective_until) : "",
    }));
  };

  const saveCellEdit = async () => {
    if (!cellEdit || !cellEdit.itemId || !cellEdit.from) return;
    const body = {
      effective_from: cellEdit.from,
      effective_until: cellEdit.until || null,
      item_id: cellEdit.itemId,
    };
    setSaving(true);
    const resp =
      cellEdit.mode === "update" && cellEdit.entry
        ? await onUpdateEntry(cellEdit.entry.id, body)
        : await onCreateEntry(cellEdit.slot.id, body);
    setSaving(false);
    if (resp) setCellEdit(null);
  };

  const cellEditItemOptions = useMemo(() => {
    if (!cellEdit) return [];
    const pool = cellEdit.slot.item_type === "product_part" ? productParts : products;
    return (pool || [])
      .filter((p) => p.active !== false)
      .map((p) => ({ value: p.id, label: p.name || `#${p.id}` }));
  }, [cellEdit, products, productParts]);

  const cellStyle = { minWidth: 130, fontSize: "0.78rem", verticalAlign: "top" };
  const stickyStyle = {
    position: "sticky",
    left: 0,
    background: "#fff",
    zIndex: 1,
    minWidth: 170,
    fontSize: "0.8rem",
    boxShadow: "2px 0 3px -1px rgba(0,0,0,0.08)",
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-2">
        <Button size="sm" variant="outline-secondary" onClick={() => setYear((y) => y - 1)}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </Button>
        <strong className="mx-3" style={{ fontSize: "1.1rem" }}>{year}</strong>
        <Button size="sm" variant="outline-secondary" onClick={() => setYear((y) => y + 1)}>
          <FontAwesomeIcon icon={faChevronRight} />
        </Button>
        <HelpTip title="La vue d'ensemble" placement="right">
          <p className="mb-1">
            La carte de l'année, comme dans le Google Sheet : une ligne par slot, une colonne
            par mois. Chaque cellule montre le produit actif au 1er du mois ; une bascule en
            cours de mois s'affiche « → produit (jour/mois) ».
          </p>
          <p className="mb-1">
            Les cellules <span className="text-primary font-weight-bold">bleues</span> signalent
            un changement par rapport au mois précédent. Cliquez sur un nom de slot pour ouvrir
            sa gestion.
          </p>
          <p className="mb-0">
            <strong>Cliquez sur une cellule</strong> pour modifier directement le produit ou la
            date de fin de la rotation en place, ou créer une nouvelle rotation à partir de ce
            mois.
          </p>
        </HelpTip>
        <span className="text-muted small ml-auto">
          {slots.length} slot(s) — produits résolus par le calendrier
        </span>
      </div>

      {periods.length > 0 && (
        <div className="d-flex align-items-center flex-wrap mb-2" style={{ gap: 6 }}>
          <span className="font-weight-bold" style={{ fontSize: "0.85rem" }}>
            Périodes {year} :
          </span>
          {periods.map((p) => {
            const isCarte = p.count >= 8; // les vraies cartes vs rotations isolées
            return (
              <Button
                key={`${p.from}|${p.until || ""}`}
                size="sm"
                variant={isCarte ? "primary" : "outline-secondary"}
                style={{ fontSize: "0.72rem", padding: "1px 8px" }}
                title={
                  onUpdatePeriod
                    ? "Modifier cette période pour tous les slots qui la portent"
                    : undefined
                }
                onClick={() =>
                  onUpdatePeriod &&
                  setPeriodModal({
                    old_from: p.from,
                    old_until: p.until,
                    new_from: p.from,
                    new_until: p.until || "",
                    count: p.count,
                  })
                }
              >
                {frDate(p.from)} → {p.until ? frDate(p.until) : "ouverte"}
                <Badge variant="light" className="ml-1">{p.count}</Badge>
              </Button>
            );
          })}
          <HelpTip title="Périodes globales" placement="right">
            <p className="mb-1">
              Les couples début / fin portés par les rotations de l'année, tous slots
              confondus. En <strong>bleu</strong> : les périodes partagées par 8 slots ou plus —
              les « cartes » (Hiver, Été, Automne…) ; les autres sont des rotations isolées
              (viennoiseries mensuelles…). Le nombre = combien de rotations portent ces dates.
            </p>
            <p className="mb-0">
              <strong>Cliquez sur une période</strong> pour décaler ou prolonger ses dates sur
              tous les slots d'un coup (même action que la carte « Périodes de rotation » de la
              vue Gestion).
            </p>
          </HelpTip>
        </div>
      )}

      {!slots.length ? (
        <p className="text-muted">Aucun slot pour le moment.</p>
      ) : (
        <div style={{ overflowX: "auto", border: "1px solid #dee2e6", borderRadius: 4 }}>
          <Table size="sm" bordered className="mb-0" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
            <thead>
              <tr>
                <th style={{ ...stickyStyle, zIndex: 2 }}>Slot</th>
                {MONTH_LABELS.map((label, i) => {
                  const ym = `${year}-${String(i + 1).padStart(2, "0")}`;
                  return (
                    <th
                      key={label}
                      style={{ ...cellStyle, textAlign: "center" }}
                      className={ym === nowYm ? "bg-warning" : undefined}
                    >
                      {label}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {groupNames.map((group) => (
                <React.Fragment key={group}>
                  <tr>
                    <td colSpan={13} className="bg-light font-weight-bold" style={{ fontSize: "0.85rem" }}>
                      {group}
                    </td>
                  </tr>
                  {rows[group].map(({ slot, cells }) => (
                    <tr key={slot.id}>
                      <td style={stickyStyle}>
                        <span
                          role="button"
                          className="text-primary"
                          style={{ cursor: "pointer" }}
                          onClick={() => onSelectSlot && onSelectSlot(slot.id)}
                          title="Ouvrir la gestion de ce slot"
                        >
                          {shortName(slot)}
                        </span>
                        {slot.item_type === "product_part" && (
                          <Badge variant="secondary" className="ml-1">part</Badge>
                        )}
                        {(slot.package_lines || []).length > 0 && (
                          <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                            {slot.package_lines.length} forfait(s)
                          </div>
                        )}
                      </td>
                      {cells.map((cell) => (
                        <td
                          key={cell.ym}
                          style={{
                            ...cellStyle,
                            ...(cell.ym === nowYm ? { background: "#fff9e6" } : {}),
                            ...(canEdit ? { cursor: "pointer" } : {}),
                          }}
                          className={cell.changed ? "text-primary font-weight-bold" : undefined}
                          onClick={() => openCellEdit(slot, cell)}
                          title={canEdit ? "Modifier le produit ou la date de fin de ce slot" : undefined}
                        >
                          {cell.startName || <span className="text-muted font-weight-normal">—</span>}
                          {cell.switches.map((s, i) => (
                            <div key={i}>
                              → {s.name} <span className="text-muted font-weight-normal">({s.day}/{cell.ym.slice(5, 7)})</span>
                            </div>
                          ))}
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {marginRows.length > 0 && (
        <div className="mt-4">
          <div className="d-flex align-items-center mb-2">
            <strong>Prix de vente &amp; marge au fil de l'année</strong>
            <HelpTip title="Marge et rotations" placement="right">
              <p className="mb-1">
                Pour chaque forfait et groupe qui porte des slots : le <strong>prix de vente HT</strong>{" "}
                de la fiche (fixe) et, mois par mois, le <strong>coût matière</strong> de la
                composition résolue au 1er du mois — les rotations font tourner les produits, donc
                le coût et la <strong>marge</strong> bougent avec la carte.
              </p>
              <p className="mb-0">
                Coût = prix d'achat saisi sur la fiche, sinon somme des prix d'achat des produits
                composants. En <span className="text-primary font-weight-bold">bleu</span> : le coût
                change vs le mois précédent. ⚠ : composants sans prix d'achat (coût partiel) ou slot
                non résolu. En <span style={{ color: "#c0392b", fontWeight: 600 }}>rouge</span> :
                marge négative.
              </p>
            </HelpTip>
            <span className="text-muted small ml-auto">
              {marginRows.length} offre(s) à slots — indicatif
            </span>
          </div>
          <div style={{ overflowX: "auto", border: "1px solid #dee2e6", borderRadius: 4 }}>
            <Table size="sm" bordered className="mb-0" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
              <thead>
                <tr>
                  <th style={{ ...stickyStyle, zIndex: 2 }}>Offre — Vente HT</th>
                  {MONTH_LABELS.map((label, i) => {
                    const ym = `${year}-${String(i + 1).padStart(2, "0")}`;
                    return (
                      <th
                        key={label}
                        style={{ ...cellStyle, textAlign: "center" }}
                        className={ym === nowYm ? "bg-warning" : undefined}
                      >
                        {label}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {marginRows.map(({ ref, isGroup, sale, cells }) => (
                  <tr key={`${isGroup ? "g" : "p"}_${ref.id}`}>
                    <td style={stickyStyle}>
                      {ref.name || `#${ref.id}`}
                      {isGroup && <Badge variant="dark" className="ml-1">groupe</Badge>}
                      {ref.in_tender_catalog && <Badge variant="warning" className="ml-1">AO</Badge>}
                      <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                        Vente {sale !== null ? centimesToCurrency(sale) : "—"}
                      </div>
                    </td>
                    {cells.map((cell) => (
                      <td
                        key={cell.ym}
                        style={{
                          ...cellStyle,
                          textAlign: "center",
                          ...(cell.ym === nowYm ? { background: "#fff9e6" } : {}),
                        }}
                        className={cell.changed ? "text-primary font-weight-bold" : undefined}
                        title={
                          cell.cost !== null
                            ? `Coût matière ${centimesToCurrency(cell.cost)}${cell.margin !== null ? ` — marge ${centimesToCurrency(cell.margin)}` : ""}`
                            : "Coût matière indisponible (aucun prix d'achat)"
                        }
                      >
                        {cell.cost === null ? (
                          <span className="text-muted font-weight-normal">—</span>
                        ) : (
                          <>
                            {centimesToCurrency(cell.cost)}
                            {(!cell.complete || cell.unresolved > 0) && (
                              <span style={{ color: "#b8860b" }}> ⚠</span>
                            )}
                            {cell.rate !== null && (
                              <div
                                className="font-weight-normal"
                                style={{
                                  fontSize: "0.7rem",
                                  color: cell.margin < 0 ? "#c0392b" : "#6c757d",
                                  fontWeight: cell.margin < 0 ? 600 : undefined,
                                }}
                              >
                                marge {(Math.round(cell.rate * 10) / 10).toFixed(1)} %
                              </div>
                            )}
                          </>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}

      <Modal show={!!periodModal} onHide={() => setPeriodModal(null)} centered>
        {periodModal && (
          <>
            <Modal.Header closeButton>
              <Modal.Title style={{ fontSize: "1rem" }}>
                Période {frDate(periodModal.old_from)} →{" "}
                {periodModal.old_until ? frDate(periodModal.old_until) : "ouverte"}
                <div className="text-muted" style={{ fontSize: "0.8rem", fontWeight: 400 }}>
                  {periodModal.count} rotation(s) portent exactement ces dates.
                </div>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Row>
                <Col>
                  <Form.Label className="mb-1 small font-weight-bold">Nouveau début</Form.Label>
                  <Form.Control
                    type="date"
                    value={periodModal.new_from}
                    onChange={(e) => setPeriodModal({ ...periodModal, new_from: e.target.value })}
                  />
                </Col>
                <Col>
                  <Form.Label className="mb-1 small font-weight-bold">Nouvelle fin (vide = ouverte)</Form.Label>
                  <Form.Control
                    type="date"
                    value={periodModal.new_until || ""}
                    onChange={(e) => setPeriodModal({ ...periodModal, new_until: e.target.value })}
                  />
                </Col>
              </Row>
              <small className="text-muted d-block mt-2">
                Décale les {periodModal.count} rotation(s) sur <strong>tous les slots</strong> qui
                portent exactement ces dates. Les devis existants ne sont pas modifiés (cascade 🔄
                slot par slot si besoin).
              </small>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={() => setPeriodModal(null)}>
                Annuler
              </Button>
              <Button variant="primary" onClick={savePeriodModal} disabled={saving || !periodModal.new_from}>
                Appliquer à tous les slots
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>

      <Modal show={!!cellEdit} onHide={() => setCellEdit(null)} centered>
        {cellEdit && (
          <>
            <Modal.Header closeButton>
              <Modal.Title style={{ fontSize: "1rem" }}>
                {cellEdit.slot.name}
                {cellEdit.entry ? (
                  <div className="text-muted" style={{ fontSize: "0.8rem", fontWeight: 400 }}>
                    Rotation en place : {cellEdit.entry.name} — du {frDate(cellEdit.entry.effective_from)}
                    {cellEdit.entry.effective_until ? ` au ${frDate(cellEdit.entry.effective_until)}` : " (ouverte)"}
                  </div>
                ) : (
                  <div className="text-muted" style={{ fontSize: "0.8rem", fontWeight: 400 }}>
                    Aucune rotation à cette date.
                  </div>
                )}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {cellEdit.entry && (
                <div className="mb-3">
                  <Form.Check
                    type="radio"
                    id="cell-edit-update"
                    label="Modifier la rotation en place (produit / dates)"
                    checked={cellEdit.mode === "update"}
                    onChange={() => setCellEditMode("update")}
                  />
                  <Form.Check
                    type="radio"
                    id="cell-edit-create"
                    label="Créer une nouvelle rotation à partir de ce mois"
                    checked={cellEdit.mode === "create"}
                    onChange={() => setCellEditMode("create")}
                  />
                </div>
              )}
              <Form.Label className="mb-1 small font-weight-bold">
                {cellEdit.slot.item_type === "product_part" ? "Part de produit" : "Produit"}
              </Form.Label>
              <Select
                options={cellEditItemOptions}
                value={cellEditItemOptions.find((o) => o.value === cellEdit.itemId) || null}
                onChange={(o) => setCellEdit({ ...cellEdit, itemId: o ? o.value : null })}
                placeholder="Choisir…"
                isClearable
                className="mb-3"
              />
              <Row>
                <Col>
                  <Form.Label className="mb-1 small font-weight-bold">Du</Form.Label>
                  <Form.Control
                    type="date"
                    value={cellEdit.from}
                    onChange={(e) => setCellEdit({ ...cellEdit, from: e.target.value })}
                  />
                </Col>
                <Col>
                  <Form.Label className="mb-1 small font-weight-bold">Jusqu'au (optionnel)</Form.Label>
                  <Form.Control
                    type="date"
                    value={cellEdit.until}
                    onChange={(e) => setCellEdit({ ...cellEdit, until: e.target.value })}
                  />
                </Col>
              </Row>
              {cellEdit.mode === "update" && cellEdit.entry && (
                <small className="text-muted d-block mt-2">
                  La modification vaut pour tous les forfaits et groupes qui portent ce slot.
                  Les devis existants ne changent pas (cascade 🔄 depuis la gestion du slot si
                  besoin).
                </small>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={() => setCellEdit(null)}>
                Annuler
              </Button>
              <Button
                variant="primary"
                onClick={saveCellEdit}
                disabled={saving || !cellEdit.itemId || !cellEdit.from}
              >
                {cellEdit.mode === "update" && cellEdit.entry ? "Mettre à jour" : "Créer la rotation"}
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </div>
  );
};

export default CarteOverview;
